/**
 * Tender narrative — Messagerie payloads for every step of the AO funnel.
 *
 * 01/06/2026 — Each step is signed by the senior-most commercial role
 * the player has hired, falling back to Léa Bertrand at junior level.
 *
 * Stages :
 *   1. announce       — when a new AO appears on the board
 *   2. submitted      — confirmation that the player's offer was sent
 *   3. counterProposal — when the client comes back with a price tweak
 *   4. won / lost     — final outcome
 *
 * Idempotency key conventions (caller is responsible) :
 *   tender-${tenderId}-announce-${spawnDay}
 *   tender-${tenderId}-submitted-${day}
 *   tender-${tenderId}-counter-${day}
 *   tender-${tenderId}-result-${day}
 */

function pickCommercialSigner(hiredSupport = {}) {
  if (hiredSupport['role-sylvie-marchand']) {
    return { senderRole: 'role-sylvie-marchand', name: 'Sylvie Marchand', title: 'Directrice commerciale' };
  }
  if (hiredSupport['role-marc-dupuis']) {
    return { senderRole: 'role-marc-dupuis', name: 'Marc Dupuis', title: 'Commercial terrain' };
  }
  if (hiredSupport['role-lea-bertrand']) {
    return { senderRole: 'role-lea-bertrand', name: 'Léa Bertrand', title: 'Commerciale junior' };
  }
  // 25/07/2026 (retour user prod) — plus AUCUN commercial recruté ne doit
  // signer à la place d'un salarié inexistant : expéditeur administratif
  // neutre pour les mémos transactionnels indispensables (déposé, gagné…).
  return { senderRole: null, name: 'Secrétariat général', title: 'Suivi administratif des AO' };
}

// En-tête expéditeur : rôle support si recruté, sinon expéditeur neutre.
function signerHeader(signer) {
  return signer.senderRole
    ? { senderRole: signer.senderRole }
    : { senderType: 'advisor', senderName: signer.name };
}

/**
 * 25/07/2026 — l'alerte « clôture imminente » est un AVANTAGE des fonctions
 * commerciales : sans Léa/Marc/Sylvie recruté(e), pas de rappel.
 */
export function hasCommercialSupport(hiredSupport = {}) {
  return !!(hiredSupport['role-sylvie-marchand']
    || hiredSupport['role-marc-dupuis']
    || hiredSupport['role-lea-bertrand']);
}

export function buildTenderAnnounceMemo(tender, hiredSupport) {
  const signer = pickCommercialSigner(hiredSupport);
  return {
    ...signerHeader(signer),
    tone: 'formal',
    subject: `Appel d'offres — ${tender.clientName}`,
    body:
      `${tender.narrative.announce}\n\n` +
      `**Conditions client :**\n` +
      `• Tarif de référence : ${tender.baseDailyRevenue} €/jour/véhicule\n` +
      `• Durée imposée : **${tender.durationDays} jours** (non négociable)\n` +
      `• Flotte minimale : ${tender.requiresFleet} véhicules\n` +
      (tender.requiresRseTier && tender.requiresRseTier !== 'none'
        ? `• EcoVadis : ${tender.requiresRseTier} ou supérieur\n` : '') +
      `\nÀ vous d'évaluer si l'on candidate. Rendez-vous dans l'onglet **Appels d'Offres**.\n\n` +
      `— ${signer.name}, ${signer.title}`,
  };
}

/**
 * 16/06/2026 — Alerte de clôture imminente (demande utilisateur). Envoyée
 * une seule fois par AO (flag `expiryAlertSent` sur l'instance) quand il
 * reste ≤ 5 jours et qu'aucun dossier n'a été déposé.
 */
export function buildTenderExpiryMemo(tender, hiredSupport, daysLeft) {
  const signer = pickCommercialSigner(hiredSupport);
  const plural = daysLeft > 1 ? 's' : '';
  return {
    ...signerHeader(signer),
    tone: 'formal',
    subject: `⏳ Clôture imminente — AO ${tender.clientName}`,
    body:
      `L'appel d'offres **${tender.clientName}** (${tender.baseDailyRevenue} €/jour/véhicule, ` +
      `${tender.durationDays} jours) clôture dans **${daysLeft} jour${plural}** et nous n'avons ` +
      `pas encore déposé de dossier.\n\n` +
      `Si ce marché vous intéresse, candidatez avant la date limite — onglet **Appels d'Offres**. ` +
      `Passé ce délai, l'AO disparaîtra du tableau.\n\n` +
      `— ${signer.name}, ${signer.title}`,
  };
}

export function buildTenderSubmittedMemo(tender, hiredSupport, offer, probability) {
  const signer = pickCommercialSigner(hiredSupport);
  const probaPct = Math.round(probability * 100);
  const probaLabel = probaPct >= 70 ? 'élevées'
    : probaPct >= 50 ? 'correctes'
    : probaPct >= 30 ? 'modérées'
    : 'faibles';
  return {
    ...signerHeader(signer),
    tone: 'formal',
    subject: `Dossier déposé — ${tender.clientName}`,
    body:
      `Votre proposition vient d'être déposée auprès de ${tender.clientName} :\n\n` +
      `• Prix : **${offer.dailyPrice} €/jour/véhicule** (vs ${tender.baseDailyRevenue} €/jour de référence)\n` +
      `• Durée : ${offer.durationDays} jours\n` +
      `• Engagement HSE : ${offer.hseCommitment === 'high' ? 'fort (audit semestriel)' : offer.hseCommitment === 'medium' ? 'modéré' : 'standard'}\n\n` +
      `Mon estimation de probabilité d'obtention : **${probaLabel} (~${probaPct}%)**.\n` +
      `Le client revient sous 7 à 14 jours avec sa décision.\n\n` +
      `— ${signer.name}`,
  };
}

export function buildTenderCounterMemo(tender, hiredSupport, originalOffer, counterOffer) {
  const signer = pickCommercialSigner(hiredSupport);
  const discountPct = Math.round((1 - counterOffer.dailyPrice / originalOffer.dailyPrice) * 100);
  return {
    ...signerHeader(signer),
    tone: 'formal',
    subject: `Contre-proposition — ${tender.clientName}`,
    body:
      `${tender.clientName} est intéressé MAIS contre-propose :\n\n` +
      `• Prix : **${counterOffer.dailyPrice} €/jour** (au lieu des ${originalOffer.dailyPrice} € demandés, soit -${discountPct} %)\n` +
      `• Durée : ${counterOffer.durationDays} jours (inchangée)\n\n` +
      `Mon conseil : ${discountPct <= 8 ? "c'est un effort raisonnable, je recommande d'accepter" : "négocier davantage est risqué, à cette marge le contrat reste profitable"}.\n` +
      `À vous de trancher dans l'onglet **Appels d'Offres**.\n\n` +
      `— ${signer.name}`,
  };
}

export function buildTenderWonMemo(tender, hiredSupport, acceptedOffer) {
  const signer = pickCommercialSigner(hiredSupport);
  const totalValue = acceptedOffer.dailyPrice * acceptedOffer.durationDays;
  return {
    ...signerHeader(signer),
    tone: 'urgent',
    kind: 'success',
    subject: `🎉 Marché remporté — ${tender.clientName}`,
    body:
      `${tender.narrative.win}\n\n` +
      `**Valeur totale du contrat : ${totalValue.toLocaleString('fr-FR')} €** ` +
      `(${acceptedOffer.dailyPrice} €/jour × ${acceptedOffer.durationDays} j).\n\n` +
      `Le contrat est désormais disponible dans le **Marché** — affectez-le à un véhicule + ` +
      `chauffeur pour démarrer la prestation.\n\n` +
      `— ${signer.name}`,
  };
}

export function buildTenderLostMemo(tender, hiredSupport) {
  const signer = pickCommercialSigner(hiredSupport);
  return {
    ...signerHeader(signer),
    tone: 'formal',
    subject: `Marché non retenu — ${tender.clientName}`,
    body:
      `${tender.narrative.loss}\n\n` +
      `Ce n'est qu'un round perdu — d'autres AO ouvriront. Je reste à l'affût.\n\n` +
      `— ${signer.name}`,
  };
}
